import { Checkbox, Input } from 'antd';
import { FormikProps } from 'formik';
import React from 'react';
import { ErrorAwareLabel } from '../../../components/error-aware-label';
import { Section } from '../../../components/form-fields/section';
import { TextField } from '../../../components/form-fields/text-field';
import { ImageInput } from '../../../components/image-input';
import { useContestStore } from '../../../contexts/mobx';
import { FieldError } from '../general-tab/field-error';
import { LANGUAGES } from '../general-tab/form';
import { ContentFormValues, ImageSlot, withEnglishImages } from './form';
import style from './style.module.scss';

interface ContentTabProps {
    formik: FormikProps<ContentFormValues>;
}

/**
 * The contest's images and copy in each language: what the applications show
 * of its game, its banner, what a participant reads after an entry and once
 * it has ended, and the browser tab icon.
 */
export const ContentTab = ({ formik }: ContentTabProps) => {
    const store = useContestStore();
    const { values, setFieldValue } = formik;
    // The applications draw the game's images natively, and no SVG; the web
    // application shows the contest's own.
    const pickerOf = (field: string) =>
        field.startsWith('game.')
            ? {
                  upload: (file: File) => store.uploadImage(file),
                  accept: 'image/png, image/jpeg',
              }
            : {
                  upload: (file: File) => store.uploadWebImage(file),
                  accept: 'image/png, image/jpeg, image/svg+xml',
              };

    /** A field's path under each language, such as `game.subTitle`. */
    const pathOf = (lang: string, field: string) =>
        `translations.${lang}.${field}`;
    const errorOf = (path: string) =>
        formik.submitCount > 0 ? formik.getFieldMeta(path).error : undefined;

    // One field per row, English on the left and French on the right.
    const textRow = (
        field: string,
        label: string,
        options: { maxLength?: number; multiline?: boolean } = {}
    ) => (
        <div className={style.input_row}>
            {LANGUAGES.map((lang) => {
                const path = pathOf(lang, field);
                const value = formik.getFieldMeta<string>(path).value;
                const onChange = (
                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                ) => setFieldValue(path, e.target.value);
                return (
                    <div key={lang} className={style.input_col}>
                        <ErrorAwareLabel
                            text={`${label} ${lang}`}
                            hasError={!!errorOf(path)}
                        />
                        {options.multiline ? (
                            <Input.TextArea
                                autoSize={{ minRows: 3 }}
                                value={value}
                                onChange={onChange}
                            />
                        ) : (
                            <TextField
                                value={value}
                                maxLength={options.maxLength}
                                onChange={onChange}
                            />
                        )}
                        <FieldError error={errorOf(path)} />
                    </div>
                );
            })}
        </div>
    );

    // A shared image shows once, in the English field.
    const imageRow = (field: string, label: string, slot: ImageSlot) => (
        <div className={style.input_row}>
            {(values.sameImageInBothLanguages[slot]
                ? (['en'] as const)
                : LANGUAGES
            ).map((lang) => {
                const path = pathOf(lang, field);
                return (
                    <div key={lang} className={style.input_col}>
                        <ErrorAwareLabel
                            text={
                                values.sameImageInBothLanguages[slot]
                                    ? label
                                    : `${label} ${lang}`
                            }
                        />
                        <ImageInput
                            {...pickerOf(field)}
                            url={formik.getFieldMeta<string | null>(path).value}
                            onChange={(url) => setFieldValue(path, url)}
                            onDelete={() => setFieldValue(path, null)}
                        />
                    </div>
                );
            })}
        </div>
    );

    // French starts from the English image once it has its own again.
    const sameInBothLanguages = (slot: ImageSlot) => (
        <Checkbox
            checked={values.sameImageInBothLanguages[slot]}
            onChange={(e) => {
                if (!e.target.checked) {
                    setFieldValue(
                        'translations.fr',
                        withEnglishImages(values, slot)
                    );
                }
                setFieldValue(
                    `sameImageInBothLanguages.${slot}`,
                    e.target.checked
                );
            }}
        >
            Same image in English and French
        </Checkbox>
    );

    return (
        <div className={style.content_tab}>
            <Section title="In the applications">
                <p className="footnote_description">
                    The applications list the contest among their games. Its
                    tile shows the title from the General tab, with this image
                    and subtitle. Tapping it opens a screen with the second
                    image, the description and a button that opens the contest.
                </p>
                {imageRow('game.image', 'Tile image', 'tile')}
                {sameInBothLanguages('tile')}
                {textRow('game.subTitle', 'Subtitle', { maxLength: 255 })}
                {imageRow('game.detailImage', 'Screen image', 'screen')}
                {sameInBothLanguages('screen')}
                {textRow('game.description', 'Description', {
                    multiline: true,
                })}
                {textRow('game.buttonTitle', 'Button label', {
                    maxLength: 255,
                })}
            </Section>

            <Section title="Banner">
                <p className="footnote_description">
                    Shown at the top of the contest. Without one, the top bar
                    shows the title.
                </p>
                {imageRow('detailImageUrlQc', 'Banner', 'banner')}
                {sameInBothLanguages('banner')}
                <Checkbox
                    checked={values.sameBannerForCircleK}
                    onChange={(e) =>
                        setFieldValue('sameBannerForCircleK', e.target.checked)
                    }
                >
                    Same banner for Circle K
                </Checkbox>
                {!values.sameBannerForCircleK &&
                    imageRow('detailImageUrlCa', 'Circle K banner', 'banner')}
                {!values.sameBannerForCircleK && (
                    <p className="footnote_description">
                        Circle K shows the Couche-Tard banner in a language left
                        without its own.
                    </p>
                )}
            </Section>

            <Section title="After an entry">
                <p className="footnote_description">
                    Shown in the block a participant has just entered through.
                </p>
                {textRow('confirmationTitle', 'Title', { maxLength: 150 })}
                {textRow('confirmationBody', 'Text', { multiline: true })}
            </Section>

            <Section title="Once the contest has ended">
                <p className="footnote_description">
                    Replaces the contest after its end date. Say when and how
                    the winners are announced.
                </p>
                {imageRow('endedImageUrl', 'Image', 'ended')}
                {sameInBothLanguages('ended')}
                {textRow('endedTitle', 'Title', { maxLength: 150 })}
                {textRow('endedBody', 'Text', { multiline: true })}
            </Section>

            <Section title="Browser tab icon">
                <p className="footnote_description">
                    Shown in the browser tab on the contest's pages, open or
                    ended. The apps show no tab. Use a square SVG, or a PNG of
                    at least 32 by 32 pixels. Without one, the tab shows the web
                    application's icon.
                </p>
                {imageRow('faviconUrl', 'Icon', 'favicon')}
                {sameInBothLanguages('favicon')}
            </Section>
        </div>
    );
};
